import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { authFetch } from '../services/authFetch';
import { packagesAPI } from '../services/api';
import { Assessment } from '../types';
import MarkdownRenderer from '../components/MicroLearning/MarkdownRenderer';
import XPInfoModal from '../components/XPInfoModal';
import './AssessmentResults.css';

interface VictorySummary {
  xp_earned: number;
  rank: { rank: number; xp: number; total_ranked: number } | null;
  // Rank as of just before this session started. Used to compute the
  // "#58 → #42 (you passed 16 students)" delta on the Victory Moment.
  // null when this is the student's first-ever scoring session.
  rank_before: { rank: number; xp: number; total_ranked: number } | null;
  streak: { current: number; longest: number; alive: boolean };
  // Combo (Phase 2.1) — consecutive-correct run within this session.
  combo: { session_max: number; lifetime_max: number; is_personal_best: boolean };
  // Single-session XP record (Phase 2.2)
  session_xp_pb: { is_personal_best: boolean; prev_best: number };
  // All Personal Bests broken this session (Phase 2.2). Empty array if none.
  personal_bests: Array<{
    kind: 'combo' | 'session_xp';
    label: string;
    old: number;
    new: number;
    unit: string;
  }>;
}

interface Question {
  id: string;
  question_text: string;
  options: { [key: string]: string };
  correct_answer?: string;
  topic?: string;
  subtopic?: string;
  difficulty?: string;
}

interface QuestionResult {
  question_id: string;
  question_text: string;
  options: { [key: string]: string };
  student_answer: string;
  correct_answer: string;
  is_correct: boolean;
  is_not_covered?: boolean;
  is_unanswered?: boolean;
  topic: string;
  difficulty: string;
  explanation?: string;
  learning_objectives?: string;
}

interface Score {
  correct: number;
  total: number;
  attempted?: number;
  not_covered?: number;
  unanswered?: number;
  percentage: number;
}

interface TopicStats {
  correct: number;
  total: number;
  not_covered?: number;
  percentage: number;
}

interface BackendResults {
  session_id: string;
  score: Score;
  topic_breakdown: { [topic: string]: TopicStats };
  assessment: { id: string; title: string; type: string };
  question_results: QuestionResult[];
  recommendations: string[];
}

const AssessmentResults: React.FC = () => {
  const { student } = useAuth();
  const { theme } = useTheme();
  const dark = theme === 'dark';
  const navigate = useNavigate();
  const location = useLocation();

  const assessment: Assessment = location.state?.assessment;
  const questions: Question[] = location.state?.questions || [];
  const answers: { [questionId: string]: string } = location.state?.answers || {};
  const localScore: Score = location.state?.score || { correct: 0, total: 0, percentage: 0 };
  const isInitial: boolean = location.state?.isInitial || false;
  const isDiagnostic: boolean = location.state?.isDiagnostic || false;
  const selectedTopics: string[] = location.state?.selectedTopics || [];
  const backendResults: BackendResults | null = location.state?.backendResults || null;

  const [showStrongTopics, setShowStrongTopics] = useState(false);
  const [expandedExplanations, setExpandedExplanations] = useState<Set<string>>(new Set());
  const [victory, setVictory] = useState<VictorySummary | null>(null);
  const [showXpInfo, setShowXpInfo] = useState(false);

  // Fetch the post-quiz gamification summary. Runs once after the session lands.
  // Failures are silent — the rest of the results page works without it.
  useEffect(() => {
    const sessionId = backendResults?.session_id;
    if (!sessionId) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await authFetch(`/api/gamification/session-summary/${sessionId}`);
        if (!res.ok) return;
        const data: VictorySummary = await res.json();
        if (!cancelled) setVictory(data);
      } catch {
        // Non-critical — swallow and move on
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [backendResults?.session_id]);


  const toggleExplanation = (questionId: string) => {
    setExpandedExplanations(prev => {
      const next = new Set(prev);
      if (next.has(questionId)) next.delete(questionId);
      else next.add(questionId);
      return next;
    });
  };

  // Use backend results if available
  const score = backendResults?.score || localScore;
  const questionResults = backendResults?.question_results || [];
  const topicBreakdownFromBackend = backendResults?.topic_breakdown || {};

  // Derive subject from assessment title
  const assessmentSubject = assessment?.title?.replace(/^(Diagnostic|Knowledge Check)\s*-\s*/i, '').trim() || '';
  /** Which package this check belongs to, passed explicitly from the shell.
   *  Inferring it from `assessmentSubject` did not work: that is a display
   *  title, and the UAT one uses an em dash the strip regex above does not
   *  match — so the "UAT" test never fired and every exit went to ESLCE. */
  const packageKey: string | undefined = location.state?.packageKey;
  const homeRoute = packageKey ? `/p/${packageKey}/lessons` : '/dashboard';

  if (!assessment) {
    return (
      <div className="results-container">
        <div className="error-state">
          <h2>Results Not Found</h2>
          <p>The assessment results could not be found.</p>
          <button onClick={() => navigate(homeRoute)} className="back-button">
            Back to Dashboard
          </button>
        </div>
      </div>
    );
  }

  const getScoreColor = (percentage: number) => {
    if (percentage >= 80) return 'excellent';
    if (percentage >= 70) return 'good';
    if (percentage >= 60) return 'average';
    return 'needs-improvement';
  };

  const getScoreMessage = (percentage: number) => {
    if (isDiagnostic) {
      if (percentage >= 80) return 'Strong foundation!';
      if (percentage >= 60) return 'Good start — some areas to focus on.';
      return 'Great that you took this — now you know where to focus.';
    }
    if (percentage >= 80) return 'Excellent work!';
    if (percentage >= 70) return 'Good job!';
    if (percentage >= 60) return 'Not bad, but there\'s room for improvement.';
    return 'Keep studying and try again!';
  };

  // Build three-zone topic breakdown for diagnostic
  const getTopicZones = () => {
    const focusAreas: Array<{ topic: string; correct: number; total: number; percentage: number }> = [];
    const startLearning: Array<{ topic: string; not_covered: number }> = [];
    const lookingGood: Array<{ topic: string; correct: number; total: number; percentage: number }> = [];

    if (Object.keys(topicBreakdownFromBackend).length > 0) {
      Object.entries(topicBreakdownFromBackend).forEach(([topic, stats]) => {
        const nc = stats.not_covered || 0;
        if (stats.total === 0 && nc > 0) {
          // All questions skipped — "Not covered yet"
          startLearning.push({ topic, not_covered: nc });
        } else if (stats.total > 0 && stats.percentage < 60) {
          focusAreas.push({ topic, correct: stats.correct, total: stats.total, percentage: Math.round(stats.percentage) });
        } else if (stats.total > 0) {
          lookingGood.push({ topic, correct: stats.correct, total: stats.total, percentage: Math.round(stats.percentage) });
        }
      });
    } else {
      // Fallback: flat breakdown (no not_covered info)
      const topicStats: { [t: string]: { correct: number; total: number } } = {};
      questions.forEach(q => {
        const topic = q.topic || 'Unknown';
        if (!topicStats[topic]) topicStats[topic] = { correct: 0, total: 0 };
        topicStats[topic].total++;
        if (answers[q.id] === q.correct_answer) topicStats[topic].correct++;
      });
      Object.entries(topicStats).forEach(([topic, s]) => {
        const pct = s.total > 0 ? Math.round((s.correct / s.total) * 100) : 0;
        if (pct < 60) focusAreas.push({ topic, ...s, percentage: pct });
        else lookingGood.push({ topic, ...s, percentage: pct });
      });
    }

    return { focusAreas, startLearning, lookingGood };
  };

  const { focusAreas, startLearning, lookingGood } = getTopicZones();

  /**
   * Where "study this" goes after a knowledge check.
   *
   * `/micro-learning` is the ESLCE lesson browser. A UAT check reports its
   * subject as "UAT Quantitative" / "UAT Verbal & Analytical", which matches no
   * ESLCE subject, so that surface fell back to the student's own stream and
   * opened a Biology or Geography module — after a UAT check, from a UAT
   * recommendation.
   *
   * UAT lessons live in the package, and the check's topic is the lesson's
   * `subtopic` ("Percentages", "Analogies and Word Relationships"). Resolve it
   * to a slug and deep-link; a few check topics are deliberately broader than
   * any single lesson ("Broad quantitative follow-up"), so fall back to the
   * lesson list rather than guessing.
   */
  const navigateToModule = async (topic: string) => {
    if (packageKey) {
      try {
        const { lessons } = await packagesAPI.lessons(packageKey);
        const wanted = (topic || '').trim().toLowerCase();
        const hit = lessons.find(
          (l: any) => (l.subtopic || '').trim().toLowerCase() === wanted
        );
        navigate(hit ? `/p/${packageKey}/lessons/${hit.slug}` : homeRoute);
      } catch {
        navigate(homeRoute);
      }
      return;
    }
    navigate('/micro-learning', {
      state: { defaultSubject: assessmentSubject, defaultTopic: topic }
    });
  };

  const notCoveredCount = score.not_covered || 0;
  const unansweredCount = score.unanswered || 0;
  const attemptedCount = score.attempted || score.total;

  return (
    <div className="results-container">
      <header className="results-header">
        <button onClick={() => navigate(homeRoute)} className="ar-back">✕</button>
        <h1>{isDiagnostic ? 'Knowledge Check Results' : 'Assessment Results'}</h1>
        <p>{assessmentSubject}</p>
      </header>

      <main className="results-content">
        {/* Overall Score */}
        <section className="score-summary">
          <div className={`score-circle ${getScoreColor(score.percentage)}`}>
            <div className="score-percentage">{score.percentage}%</div>
            <div className="score-fraction">{score.correct}/{attemptedCount}</div>
          </div>
          <div className="score-details">
            <h2>{getScoreMessage(score.percentage)}</h2>
            <p>
              You answered {score.correct} out of {attemptedCount} attempted questions correctly.
              {notCoveredCount > 0 && (
                <> {notCoveredCount} {notCoveredCount === 1 ? 'topic' : 'questions'} marked as not yet covered.</>
              )}
              {unansweredCount > 0 && (
                <> {unansweredCount} {unansweredCount === 1 ? 'question' : 'questions'} not answered.</>
              )}
            </p>
          </div>
        </section>

        {/* PB celebration strip — only renders when at least one PB fired
            in this session. Sits above the standard victory tiles. */}
        {victory && victory.personal_bests && victory.personal_bests.length > 0 && (
          <section className="pb-strip" aria-live="polite">
            <div className="pb-strip-emoji">🎉</div>
            <div className="pb-strip-body">
              <div className="pb-strip-headline">
                {victory.personal_bests.length === 1
                  ? 'New personal best!'
                  : `${victory.personal_bests.length} new personal bests!`}
              </div>
              <ul className="pb-strip-list">
                {victory.personal_bests.map((pb) => (
                  <li key={pb.kind}>
                    {pb.kind === 'combo' ? '⚡' : '🚀'} <strong>{pb.label}:</strong>{' '}
                    {pb.old > 0 && (
                      <span className="pb-strip-old">{pb.old.toLocaleString()} {pb.unit}</span>
                    )}
                    {pb.old > 0 && <span className="pb-strip-arrow"> → </span>}
                    <span className="pb-strip-new">
                      {pb.new.toLocaleString()} {pb.unit}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          </section>
        )}

        {/* Victory Moment — XP earned + current rank + streak */}
        {victory && (victory.xp_earned > 0 || victory.rank) && (
          <section className="victory-banner" aria-live="polite">
            {victory.xp_earned > 0 && (
              <div className={`victory-tile victory-xp${victory.session_xp_pb?.is_personal_best ? ' victory-xp-pb' : ''}`}>
                <div className="victory-label">
                  Points earned
                  <button
                    type="button"
                    className="victory-info-btn"
                    onClick={() => setShowXpInfo(true)}
                    aria-label="How points work"
                  >
                    ⓘ
                  </button>
                </div>
                <div className="victory-value">+{victory.xp_earned.toLocaleString()}</div>
                {victory.session_xp_pb?.is_personal_best && (
                  <div className="victory-sub">🆕 Best session ever!</div>
                )}
              </div>
            )}
            {victory.rank && (
              <div className="victory-tile victory-rank">
                <div className="victory-label">All-time rank</div>
                <div className="victory-value">
                  {victory.rank_before && victory.rank_before.rank !== victory.rank.rank ? (
                    <>
                      <span className="victory-rank-old">#{victory.rank_before.rank}</span>
                      <span className="victory-rank-arrow"> → </span>
                      <span className="victory-rank-new">#{victory.rank.rank}</span>
                    </>
                  ) : (
                    <>#{victory.rank.rank}</>
                  )}
                </div>
                <div className="victory-sub">
                  {victory.rank_before && victory.rank_before.rank > victory.rank.rank ? (
                    <>You passed {victory.rank_before.rank - victory.rank.rank} student{victory.rank_before.rank - victory.rank.rank === 1 ? '' : 's'}!</>
                  ) : (
                    <>{victory.rank.xp.toLocaleString()} points total</>
                  )}
                </div>
              </div>
            )}
            {victory.streak.alive && victory.streak.current > 0 && (
              <div className="victory-tile victory-streak">
                <div className="victory-label">Streak</div>
                <div className="victory-value">🔥 {victory.streak.current}</div>
                <div className="victory-sub">
                  {victory.streak.current === 1 ? 'day — keep it going!' : `days in a row`}
                </div>
              </div>
            )}
            {!victory.streak.alive && victory.streak.longest > 0 && (
              <div className="victory-tile victory-streak-dormant">
                <div className="victory-label">Best streak ever</div>
                <div className="victory-value">🔥 {victory.streak.longest}</div>
                <div className="victory-sub">study tomorrow to start a new one</div>
              </div>
            )}
            {/* Combo tile — only when the session had a meaningful run */}
            {victory.combo && victory.combo.session_max >= 3 && (
              <div className={`victory-tile victory-combo${victory.combo.is_personal_best ? ' victory-combo-pb' : ''}`}>
                <div className="victory-label">
                  {victory.combo.is_personal_best ? 'New best combo!' : 'Best run'}
                </div>
                <div className="victory-value">
                  ⚡ {victory.combo.session_max}
                </div>
                <div className="victory-sub">
                  {victory.combo.is_personal_best
                    ? 'in a row — personal best'
                    : `in a row · all-time best: ${victory.combo.lifetime_max}`}
                </div>
              </div>
            )}
          </section>
        )}

        <XPInfoModal open={showXpInfo} onClose={() => setShowXpInfo(false)} />

        {/* Three-Zone Topic Breakdown (diagnostic) */}
        {isDiagnostic && (focusAreas.length > 0 || startLearning.length > 0 || lookingGood.length > 0) && (
          <>
            {/* Focus Areas — red/orange zone */}
            {focusAreas.length > 0 && (
              <section className="topic-zone focus-zone">
                <div className="zone-header">
                  <h2>Focus Areas</h2>
                  <p className="zone-subtitle">These need your attention — you attempted but scored below 60%</p>
                </div>
                <div className="topic-grid">
                  {focusAreas.map(item => (
                    <div key={item.topic} className="topic-card zone-focus">
                      <h3>{item.topic}</h3>
                      <div className="topic-score">
                        <span className="topic-percentage">{item.percentage}%</span>
                        <span className="topic-fraction">({item.correct}/{item.total})</span>
                      </div>
                      <div className="progress-bar">
                        <div className="progress-fill needs-improvement" style={{ width: `${item.percentage}%` }}></div>
                      </div>
                      <button className="topic-action focus" onClick={() => navigateToModule(item.topic)}>
                        Study this topic →
                      </button>
                    </div>
                  ))}
                </div>
              </section>
            )}

            {/* Start Learning — blue zone */}
            {startLearning.length > 0 && (
              <section className="topic-zone not-covered-zone">
                <div className="zone-header">
                  <h2>Start Learning</h2>
                  <p className="zone-subtitle">Topics you haven't studied yet</p>
                </div>
                <div className="topic-grid">
                  {startLearning.map(item => (
                    <div key={item.topic} className="topic-card zone-not-covered">
                      <h3>{item.topic}</h3>
                      <span className="not-covered-label">Not covered yet</span>
                      <button className="topic-action learn" onClick={() => navigateToModule(item.topic)}>
                        Start learning →
                      </button>
                    </div>
                  ))}
                </div>
              </section>
            )}

            {/* Looking Good — green zone (collapsed by default) */}
            {lookingGood.length > 0 && (
              <section className="topic-zone solid-zone">
                <div className="zone-header clickable" onClick={() => setShowStrongTopics(!showStrongTopics)}>
                  <h2>Looking Good ({lookingGood.length} {lookingGood.length === 1 ? 'topic' : 'topics'})</h2>
                  <span className="zone-toggle">{showStrongTopics ? '▲' : '▼'}</span>
                </div>
                {showStrongTopics && (
                  <div className="topic-grid">
                    {lookingGood.map(item => (
                      <div key={item.topic} className="topic-card zone-solid">
                        <h3>{item.topic}</h3>
                        <div className="topic-score">
                          <span className="topic-percentage">{item.percentage}%</span>
                          <span className="topic-fraction">({item.correct}/{item.total})</span>
                        </div>
                        <div className="progress-bar">
                          <div className="progress-fill excellent" style={{ width: `${item.percentage}%` }}></div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </section>
            )}
          </>
        )}

        {/* Flat topic breakdown (non-diagnostic) */}
        {!isDiagnostic && Object.keys(topicBreakdownFromBackend).length > 0 && (
          <section className="topic-breakdown">
            <h2>Performance by Topic</h2>
            <div className="topic-grid">
              {Object.entries(topicBreakdownFromBackend).map(([topic, stats]) => (
                <div key={topic} className="topic-card">
                  <h3>{topic}</h3>
                  <div className="topic-score">
                    <span className="topic-percentage">{Math.round(stats.percentage)}%</span>
                    <span className="topic-fraction">({stats.correct}/{stats.total})</span>
                  </div>
                  <div className="progress-bar">
                    <div
                      className={`progress-fill ${getScoreColor(stats.percentage)}`}
                      style={{ width: `${stats.percentage}%` }}
                    ></div>
                  </div>
                </div>
              ))}
            </div>
          </section>
        )}

        {/* Question-by-Question Review */}
        {questionResults.length > 0 && (
          <section className="question-details">
            <h2>Question-by-Question Review</h2>
            <div className="questions-list">
              {questionResults.map((result, index) => {
                // Use frontend answers as authoritative source for what student picked
                const studentPick = answers[result.question_id] || result.student_answer || '';
                const isNotCovered = studentPick === 'NOT_COVERED' || result.is_not_covered;
                const isUnanswered = result.is_unanswered || (!studentPick && !isNotCovered);
                const wasCorrect = !isUnanswered && studentPick === result.correct_answer;
                const cardClass = isNotCovered ? 'not-covered' : isUnanswered ? 'unanswered' : wasCorrect ? 'correct' : 'incorrect';
                const isExpanded = expandedExplanations.has(result.question_id);

                return (
                  <div key={result.question_id} className={`question-card ${cardClass}`}>
                    <div className="question-header">
                      <div className="question-number">Q{index + 1}</div>
                      <div className={`result-indicator ${cardClass}`}>
                        {isNotCovered ? '— Skipped' : isUnanswered ? '— Not answered' : wasCorrect ? '✓ Correct' : `✗ Incorrect (picked ${studentPick})`}
                      </div>
                    </div>

                    <div className="question-content">
                      <div className="ar-question-text">
                        <MarkdownRenderer>{result.question_text}</MarkdownRenderer>
                      </div>

                      {!isNotCovered && (
                        <>
                          <div className="ar-options">
                            {Object.entries(result.options).map(([key, value]) => {
                              const isCorrectAnswer = key === result.correct_answer;
                              const isStudentPick = !isUnanswered && key === studentPick;
                              const cls = isStudentPick && wasCorrect ? 'student-correct'
                                : isCorrectAnswer ? 'correct-answer'
                                : isStudentPick && !wasCorrect ? 'student-wrong' : '';
                              return (
                                <div
                                  key={key}
                                  className={`ar-option ${cls}`}
                                  style={isStudentPick && !wasCorrect ? { borderColor: dark ? '#ff6b6b' : '#dc2626', background: dark ? 'rgba(255, 107, 107, 0.15)' : '#fef2f2' } : isCorrectAnswer ? { borderColor: dark ? '#86c232' : '#4a7c10', background: dark ? 'rgba(134, 194, 50, 0.15)' : '#f0fdf4' } : undefined}
                                >
                                  <span
                                    className={`ar-option-letter ${cls}`}
                                    style={isStudentPick && !wasCorrect ? { background: dark ? '#ff6b6b' : '#dc2626', borderColor: dark ? '#ff6b6b' : '#dc2626', color: 'white' } : isCorrectAnswer ? { background: dark ? '#86c232' : '#4a7c10', borderColor: dark ? '#86c232' : '#4a7c10', color: dark ? '#0a0f07' : 'white' } : undefined}
                                  >{key}</span>
                                  <span className="ar-option-text"><MarkdownRenderer inline>{value}</MarkdownRenderer></span>
                                  {isStudentPick && wasCorrect && <span className="ar-option-tag correct">✓ You</span>}
                                  {isCorrectAnswer && !isStudentPick && <span className="ar-option-tag correct">✓ Answer</span>}
                                  {isStudentPick && !wasCorrect && <span className="ar-option-tag wrong">✗ You</span>}
                                </div>
                              );
                            })}
                          </div>

                          {result.explanation && (
                            <>
                              <button
                                className="ar-explain-toggle"
                                onClick={() => toggleExplanation(result.question_id)}
                              >
                                {isExpanded ? '▲ Hide explanation' : '▼ Show explanation'}
                              </button>
                              {isExpanded && (
                                <div className="ar-explanation">
                                  <MarkdownRenderer>{result.explanation}</MarkdownRenderer>
                                </div>
                              )}
                            </>
                          )}
                        </>
                      )}

                      {isNotCovered && result.topic && (
                        <div className="not-covered-action">
                          <p>You haven't covered this topic yet.</p>
                          <button className="topic-action learn" onClick={() => navigateToModule(result.topic)}>
                            Start learning {result.topic} →
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </section>
        )}

        {/* Action Buttons */}
        <section className="action-buttons">
          {isDiagnostic ? (
            <>
              <button
                onClick={() => navigate(packageKey ? homeRoute : '/diagnostic-onboarding')}
                className="primary-button"
              >
                Check another subject
              </button>
              <button
                onClick={() => navigate(homeRoute)}
                className="secondary-button"
              >
                Go to Dashboard
              </button>
            </>
          ) : (
            <>
              <button
                onClick={() => navigate(homeRoute)}
                className="primary-button"
              >
                Go to Dashboard
              </button>
              {focusAreas.length > 0 && assessmentSubject && (
                <button
                  onClick={() =>
                  packageKey
                    ? navigate(homeRoute)
                    : navigate('/micro-learning', {
                        state: { defaultSubject: assessmentSubject },
                      })
                }
                  className="secondary-button"
                >
                  Study {assessmentSubject}
                </button>
              )}
            </>
          )}
        </section>
      </main>
    </div>
  );
};

export default AssessmentResults;
